/**
 * People who open this app from One Manager sign in only there: they have no password here. When
 * their session ends (sign-out, expiry, inactivity) they see why, with a button back to One
 * Manager: this app never sends them there by itself, and never shows them its login page. The
 * marker outlives sign-out on purpose, so a later expired visit still knows who they are; a
 * sign-in on this app's own login page clears it.
 */

const HUB_SESSION_KEY = 'isSsoUser';

/** One Manager's web app (VITE_PLATFORM_URL), without a trailing slash. */
export const PLATFORM_URL = import.meta.env.VITE_PLATFORM_URL?.replace(/\/$/, '') || null;

/** Where a person who came from One Manager goes back to. */
export const PLATFORM_HOME = PLATFORM_URL ? `${PLATFORM_URL}/dashboard` : null;

/** This BMS's id in One Manager's registry. */
export const HUB_BMS_ID = import.meta.env.VITE_HUB_BMS_ID || 'eldaved-tile';

/** One Manager's support page, for when opening this app keeps failing. */
export const PLATFORM_SUPPORT = PLATFORM_URL ? `${PLATFORM_URL}/dashboard/support` : null;

/**
 * One Manager's page that opens this app again for a business with a fresh sign-in link (each link
 * works once), counting the attempt against the number the admin hub allows.
 */
export function reopenUrl(tenantId, attempt) {
  if (!PLATFORM_URL || !tenantId) return null;
  const query = new URLSearchParams({ bms: HUB_BMS_ID, tenant: tenantId, attempt: String(attempt) });
  return `${PLATFORM_URL}/dashboard/open?${query}`;
}

/** The business a One Manager sign-in link is for (read, not trusted: only to offer a retry). */
export function tenantOfLink(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).tenantId || null;
  } catch {
    return null;
  }
}

export function markHubSession() {
  try {
    localStorage.setItem(HUB_SESSION_KEY, 'true');
  } catch {
    // storage unavailable: they will see the login page instead
  }
}

export function clearHubSession() {
  try {
    localStorage.removeItem(HUB_SESSION_KEY);
  } catch {
    // nothing to clear
  }
}

export function isHubSession() {
  try {
    return localStorage.getItem(HUB_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Why a session ended, as the session-ended screen explains it. */
export const END_REASONS = ['signed-out', 'inactive', 'expired', 'no-session', 'closed'];

/** The in-app screen that says why a hub user's session ended, with a way back to One Manager. */
export const sessionEndedPath = (reason, call) => {
  const known = END_REASONS.includes(reason) ? reason : 'expired';
  return `/session-ended?reason=${known}${call ? `&call=${encodeURIComponent(call)}` : ''}`;
};

/**
 * Where a signed-out person goes. Hub users stop on the session-ended screen, which says why and
 * offers a button back to One Manager. Returns false for this app's own accounts, whose caller
 * shows the login page. `call` is the request that was refused, shown to help find the cause;
 * `go` is the navigation, replaceable in tests.
 */
export function leaveSignedOut(reason = 'signed-out', { call, go = (url) => window.location.replace(url) } = {}) {
  if (!isHubSession()) return false;
  go(sessionEndedPath(reason, call));
  return true;
}

// --------------------------------------------------------------------------------------
// Confirming a sensitive action through One Manager (hub docs/04 §3.3). Someone who signs in there
// has no password here, so where this app asks for one they confirm with their One Manager
// password instead. What they were doing is kept for this tab while they are away; One Manager
// sends them to /confirm, which exchanges its token for a grant and brings them back.

const PENDING_CONFIRMATION_KEY = 'pendingConfirmation';
const PENDING_CONFIRMATION_MS = 15 * 60 * 1000;

/** The business this session belongs to in One Manager (saved at sign-in). */
export function hubTenantId(user) {
  try {
    return JSON.parse(localStorage.getItem('tenant') || 'null')?.id || user?.tenant_id || null;
  } catch {
    return user?.tenant_id || null;
  }
}

function readPending() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_CONFIRMATION_KEY) || 'null');
    if (pending && Date.now() - pending.at < PENDING_CONFIRMATION_MS) return pending;
  } catch {
    // storage unavailable: nothing is pending
  }
  return null;
}

function writePending(pending) {
  try {
    if (pending) sessionStorage.setItem(PENDING_CONFIRMATION_KEY, JSON.stringify(pending));
    else sessionStorage.removeItem(PENDING_CONFIRMATION_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sends the person to One Manager to confirm `action` ("module-reset:retail"); `what` names it for
 * them there ("reset the Retail module"), `state` is what to restore on return. Returns false when
 * it can't (no One Manager address or business known), for the caller to say so.
 */
export function confirmThroughPlatform(
  { action, what, state, user },
  { go = (url) => window.location.assign(url), returnTo = `${window.location.pathname}${window.location.search}` } = {}
) {
  const tenantId = hubTenantId(user);
  if (!PLATFORM_URL || !tenantId) return false;
  if (!writePending({ action, state, returnTo, at: Date.now() })) return false;
  const query = new URLSearchParams({ bms: HUB_BMS_ID, tenant: tenantId, action, what });
  go(`${PLATFORM_URL}/dashboard/confirm?${query}`);
  return true;
}

/** Where /confirm returns to: the page that asked (it is only ever a path in this app). */
export function pendingReturnPath() {
  const returnTo = readPending()?.returnTo;
  return typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
}

/** /confirm stores the grant it got for the pending action. */
export function storeGrant(action, confirmation) {
  const pending = readPending();
  if (!pending || pending.action !== action) return false;
  return writePending({ ...pending, confirmation });
}

/** The page that asked takes its grant and saved state back, once. */
export function takeConfirmation(action) {
  const pending = readPending();
  if (!pending || pending.action !== action || !pending.confirmation) return null;
  writePending(null);
  return { confirmation: pending.confirmation, state: pending.state || {} };
}
